import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * A single hover control per row, revealed on hover, opening the actions for *that* row.
 *
 * One menu rather than an icon per action: a project row carries three (new sub-project,
 * knowledge base, settings) and three icons would not fit beside an indented, truncated name at
 * nav width. A menu also lets each action carry a written label instead of relying on an icon
 * to convey it.
 *
 * Per row rather than once at the top, because the row *is* the subject — the create dialogs
 * never have to ask "under what?", and Settings is unambiguous in a way a global link isn't
 * once the sidebar shows a whole tree of projects. Shared between the project tree in the sidebar
 * and the page tree in the knowledge base panel, so a row menu reads the same in both.
 */
export function RowMenu({
  items, label, tone,
}: {
  items: Array<{ label: string; onClick: () => void; separated?: boolean }>;
  label: string;
  tone: 'default' | 'active';
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Capture phase, and on scroll too: the menu is positioned from a rect taken when it opened,
    // so any scroll of the nav would otherwise leave it floating away from its row.
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.bottom + 4, left: rect.left });
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggle}
        title={label}
        aria-label={label}
        className={cn(
          'w-5 h-5 mr-1 flex items-center justify-center rounded flex-shrink-0 transition-opacity',
          open ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100',
          tone === 'active' ? 'text-orange-100 hover:bg-white/25' : 'text-ink-faint hover:bg-surface-muted hover:text-ink',
        )}
      >
        <MoreHorizontal size={12} />
      </button>

      {/* Portalled and fixed-positioned: the nav scrolls and the sidebar is z-indexed, so a
          menu rendered inline would be clipped by the scroll container. */}
      {open && anchor &&
        createPortal(
          <div
            style={{ top: anchor.top, left: anchor.left }}
            className="fixed z-50 min-w-[170px] py-1 rounded-lg border border-border bg-card shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.label}
                onClick={() => { setOpen(false); item.onClick(); }}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-xs text-ink-muted hover:bg-surface-subtle hover:text-ink transition-colors',
                  item.separated && 'border-t border-border-subtle mt-1 pt-2',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

